import {
  useState,
  useRef,
  useEffect,
  useCallback,
  useLayoutEffect,
} from 'react';

const DESKTOP_BP = 1024;
// Must match the `transition: opacity` duration in WCCountryDetail.module.scss.
const FADE_TRANSITION_MS = 200;

/**
 * Maneja el toggle de "Ver plantilla completa" con un remount controlado en
 * mobile/tablet para evitar el bug de paint stale del browser cuando crece
 * el contenido con position:sticky activo.
 *
 * Flujo en mobile/tablet:
 *   1. Click → fade out (opacity 1 → 0, 200ms) — Squad sigue montado.
 *   2. Fade out completo → desmontar Squad (return null).
 *   3. Doble rAF → remontar Squad con nuevo state (todavía invisible).
 *   4. Doble rAF → fade in (opacity 0 → 1, 200ms).
 *   5. Antes del paint del fade-in, restaurar scrollTop.
 *
 * En desktop el toggle es un setState directo, sin remount.
 *
 * @returns {{
 *   showFullSquad: boolean,
 *   isSquadHidden: boolean,
 *   isSquadMounted: boolean,
 *   handleSquadToggleChange: (value: boolean) => void,
 *   resetSquadToggle: () => void,
 * }}
 */
export const useSquadToggleRemount = () => {
  const [showFullSquad, setShowFullSquad] = useState(false);
  const [isSquadHidden, setIsSquadHidden] = useState(false);
  const [isSquadMounted, setIsSquadMounted] = useState(true);

  const pendingValueRef = useRef(null);
  const savedScrollRef = useRef(null);

  const [isDesktop, setIsDesktop] = useState(
    typeof window !== 'undefined' ? window.innerWidth >= DESKTOP_BP : true,
  );

  useEffect(() => {
    const check = () => setIsDesktop(window.innerWidth >= DESKTOP_BP);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const handleSquadToggleChange = useCallback(
    value => {
      if (isDesktop) {
        setShowFullSquad(value);
        return;
      }

      // Mobile/tablet: capture scroll, store pending value, kick off fade-out.
      // Squad stays mounted while opacity transitions; the next effect handles
      // the unmount/remount/fade-in sequence.
      const container = document.querySelector('.content_div');
      savedScrollRef.current = container ? container.scrollTop : null;
      pendingValueRef.current = value;
      setIsSquadHidden(true);
    },
    [isDesktop],
  );

  // Sequence: wait for the CSS fade-out to finish, then unmount Squad, paint
  // the empty (invisible) frame, remount with the new value, paint that frame
  // (still invisible), then start the fade-in.
  useEffect(() => {
    if (!isSquadHidden) return undefined;
    if (pendingValueRef.current === null) return undefined;

    const fadeOutTimeoutId = setTimeout(() => {
      // Phase 2: fade-out done (opacity 0). Unmount Squad.
      setIsSquadMounted(false);

      // Phase 3: wait two frames for the empty paint to settle, then remount
      // with the new value. The remount paint stays invisible (opacity 0).
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const newValue = pendingValueRef.current;
          pendingValueRef.current = null;
          setShowFullSquad(newValue);
          setIsSquadMounted(true);

          // Phase 4: wait two more frames for the new content to paint
          // (still invisible), then start the fade-in.
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              setIsSquadHidden(false);
            });
          });
        });
      });
    }, FADE_TRANSITION_MS);

    return () => clearTimeout(fadeOutTimeoutId);
  }, [isSquadHidden]);

  // Restore scrollTop right when fade-in starts. useLayoutEffect runs sync
  // before paint, so the user never sees a wrong scroll position appear and
  // then jump.
  useLayoutEffect(() => {
    if (isSquadHidden) return;
    if (savedScrollRef.current !== null) {
      const container = document.querySelector('.content_div');
      if (container) container.scrollTop = savedScrollRef.current;
      savedScrollRef.current = null;
    }
  }, [isSquadHidden]);

  const resetSquadToggle = useCallback(() => {
    setShowFullSquad(false);
    setIsSquadHidden(false);
    setIsSquadMounted(true);
    pendingValueRef.current = null;
    savedScrollRef.current = null;
  }, []);

  return {
    showFullSquad,
    isSquadHidden,
    isSquadMounted,
    handleSquadToggleChange,
    resetSquadToggle,
  };
};
